import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import axios from '../../../config/axios';
import {
  WhatsAppInboxState,
  ConversationsResponse,
  MessagesResponse,
  ListConversationsFilters,
  SendMessageRequest,
  MessageStatus,
  SendMessageResponse,
  MessageDirection,
  MessageType,
  WhatsappInboxTabEnum,
  WhatsappProfileTabEnum,
  GetApplicationHistoryResponseData,
  ConversationNoteDto,
  ListConversationNotesResponse,
  ConversationStatus,
  ConversationTask,
  ConversationListItemDto,
  MessageDto,
} from '../../../utils/interfaces/whatsapp/WhatsappInboxTypes';
import { WHATSAPP_INBOX_DEFAULT_LIMIT } from 'src/utils/constants/constants';
import Toast from 'src/shared/toast/Toast';
import { ApplicantListItem } from 'src/utils/interfaces/applicants/ApplicantListItem';
import { ResponseResult } from 'src/utils/interfaces/http/ResponseResult';
import { replaceVariablesInTemplate } from 'src/utils/helpers/whatsapp-template-utils';
import { FullTimeJobDetails } from 'src/utils/interfaces/job/JobDetails';
import { Partner, PartnerUser } from 'src/utils/interfaces/partner/PartnerProfile';

const appKey = 'whatsappInbox';
const apiModuleUrl = 'pa/whatsapp/inbox';

// Async thunks
export const getConversations = createAsyncThunk(`${appKey}/getConversations`, async (filters: ListConversationsFilters & { append?: boolean } = {}) => {
  // Partner ID is now fetched from authenticated user on backend (secure)
  const { append, ...apiFilters } = filters;
  if (apiFilters?.search?.startsWith('+')) {
    apiFilters.search = apiFilters.search.substring(1);
  }
  const response = await axios.get<ResponseResult<ConversationsResponse>>(`${apiModuleUrl}/conversations`, { params: apiFilters });
  return { ...response.data.data, append: append || false };
}
);

export const getConversationDetails = createAsyncThunk(`${appKey}/getConversationDetails`, async (conversationId: string) => {
  // Try to fetch the conversation by ID directly first (if API supports it)
  // Otherwise, fetch from conversations list
  try {
    // Try direct endpoint first
    const directResponse = await axios.get<ResponseResult<ConversationListItemDto>>(`${apiModuleUrl}/conversations/${conversationId}`);
    if (directResponse.data.data) {
      return directResponse.data.data;
    }
  } catch (error) {
    // If direct endpoint doesn't exist, fall back to fetching from list
    // Fetch with a reasonable limit to find the conversation
    const response = await axios.get<ResponseResult<ConversationsResponse>>(`${apiModuleUrl}/conversations`, {
      params: { limit: 100, page: 1 }
    });
    // Find the specific conversation in the response
    const conversation = response.data.data.conversations.find((c) => c.id === conversationId);
    if (!conversation) {
      throw new Error(`Conversation ${conversationId} not found`);
    }
    return conversation;
  }
  throw new Error(`Conversation ${conversationId} not found`);
});

export const getMessages = createAsyncThunk(`${appKey}/getMessages`, async ({ conversationId, page = 1, limit = 50, type, }: {
  conversationId: string;
  page?: number;
  limit?: number;
  type?: 'recruiter' | 'saud';
}) => {
  const response = await axios.get<ResponseResult<MessagesResponse>>(`${apiModuleUrl}/conversations/${conversationId}/messages`, { params: { page, limit, type }, });
  return { conversationId, ...response.data.data };
});

export const getMessagesCvSearch = createAsyncThunk(`${appKey}/getMessagesCvSearch`, async ({ workerExternalId, page = 1, limit = 50, type, }: {
  workerExternalId: string;
  page?: number;
  limit?: number;
  type?: 'recruiter' | 'saud';
}) => {
  const response = await axios.get<ResponseResult<MessagesResponse>>(`${apiModuleUrl}/workers/${workerExternalId}/messages`, { params: { page, limit, type }, });
  return { workerExternalId, ...response.data.data };
});


export const sendMessage = createAsyncThunk(`${appKey}/sendMessage`, async ({ conversationId, tempMessageId, content, templateId, templateData }: { conversationId: string; tempMessageId: string; content: string; templateId?: string; templateData: { jobDetails: FullTimeJobDetails; partner: Partner; selectedApplicant: any; partnerUser: PartnerUser; } }) => {
  const response = await axios.post(`${apiModuleUrl}/conversations/${conversationId}/send`, { content: templateId ? undefined : content, templateId });
  return { conversationId, tempMessageId, templateData, message: response.data.data as SendMessageResponse };
}
);

export const assignConversation = createAsyncThunk(`${appKey}/assignConversation`, async ({ conversationId, assignedToUserId }: { conversationId: string; assignedToUserId: number | null; }) => {
  const response = await axios.put(`${apiModuleUrl}/conversations/${conversationId}/assign`, { assignedToUserId });
  return { conversationId, ...response.data };
});

export const sendTypingIndicator = createAsyncThunk(`${appKey}/sendTypingIndicator`, async ({ conversationId }: any) => {
  const response = await axios.post(`${apiModuleUrl}/conversations/${conversationId}/typing`);
  return { conversationId, ...response.data };
});

export const markConversationAsRead = createAsyncThunk(`${appKey}/markConversationAsRead`, async ({ conversationId }: any) => {
  const response = await axios.post(`${apiModuleUrl}/conversations/${conversationId}/mark-read`);
  return { conversationId, ...response.data };
});


export const getApplicationHistory = createAsyncThunk(`${appKey}/getApplicationHistory`, async ({ conversationId, skip = 0, limit = 50 }: { conversationId: string; skip?: number; limit?: number; }) => {
  const response = await axios.get<ResponseResult<GetApplicationHistoryResponseData>>(`${apiModuleUrl}/conversations/${conversationId}/applications`, { params: { skip, limit }, });
  return { conversationId, ...response.data.data };
});

export const getRecentApplicationDetails = createAsyncThunk(`${appKey}/getRecentApplicationDetails`, async ({ conversationId, workerExternalId }: { conversationId: string; workerExternalId?: string }) => {
  const applicationHistoryResponse = await axios.get<{ data: GetApplicationHistoryResponseData }>(`${apiModuleUrl}/conversations/${conversationId}/applications`, { params: { skip: 0, limit: 1 }, });
  const recentApplication = applicationHistoryResponse.data.data.recentApplication;
  const isOffline = recentApplication?.isOffline || false;

  if (!recentApplication && workerExternalId) {
    const response = await axios.get(`pa/ft/cv-search/${workerExternalId}/details`);
    return { conversationId, ...response.data.data } as any;
  }

  const applicantDetailsResponse = await axios.get<ResponseResult<ApplicantListItem>>(`pa/ft/job-applications/${recentApplication.externalId}/details?isOffline=${isOffline || false}`);
  return { conversationId, ...applicantDetailsResponse.data.data };
});

const initialState: WhatsAppInboxState = {
  conversations: [],
  conversationsTotal: 0,
  conversationsPage: 1,
  isLoadingConversations: false,
  isLoadingMoreConversations: false,

  activeConversationId: null,

  messages: {},
  messagesTotal: {},
  messagesPage: {},
  isLoadingMessages: false,

  notes: {},
  isLoadingNotes: false,
  isSavingNote: false,

  canSendText: {},

  filters: {
    page: 1,
    limit: WHATSAPP_INBOX_DEFAULT_LIMIT,
  },

  activeWhatsappInboxTab: WhatsappInboxTabEnum.RECRUITER,

  activeWhatsappProfileTab: WhatsappProfileTabEnum.PROFILE,

  isSendingMessage: false,
  error: null,
  rtmToken: null,
  channelId: null,
  applicationHistory: null,
  isLoadingApplicationHistory: false,
  applicationHistoryTotal: 0,
  applicationHistoryPage: 1,

  recentApplicationDetails: null,
  isLoadingRecentApplicationDetails: false,
};

const whatsappInboxSlice = createSlice({
  name: appKey,
  initialState,
  reducers: {
    setActiveConversation: (state, action: PayloadAction<string | null>) => {
      state.activeConversationId = action.payload;
    },
    setActiveWhatsappInboxTab: (state, action: PayloadAction<WhatsappInboxTabEnum>) => {
      state.activeWhatsappInboxTab = action.payload;
    },
    setActiveWhatsappProfileTab: (state, action: PayloadAction<WhatsappProfileTabEnum>) => {
      state.activeWhatsappProfileTab = action.payload;
    },
    setFilters: (state, action: PayloadAction<ListConversationsFilters>) => {
      const newFilters = { ...state.filters, ...action.payload };
      // Reset conversations when page is set to 1 (filter/search changes reset to page 1)
      if (action.payload.page === 1) {
        state.conversations = [];
      }
      state.filters = newFilters;
    },
    resetFilters: (state) => {
      state.filters = { page: 1, limit: 20 };
    },
    clearError: (state) => {
      state.error = null;
    },
    // Real-time reducers for Agora RTM events
    handleRealtimeMessage: (state, action: PayloadAction<{ conversationId: string; message: any; worker?: any }>) => {
      const { conversationId, message, worker } = action.payload;

      // Initialize messages array if it doesn't exist
      if (!state.messages[conversationId]) {
        state.messages[conversationId] = [];
      }

      // Check if message already exists (prevent duplicates)
      const exists = state.messages[conversationId].some((m) => m.id === message.id);
      if (!exists && ((message.direction === 'inbound') || (message.direction === 'outbound' && !(message.sentByUserId || message.sentByUserExternalId)))) {
        state.messages[conversationId]?.unshift(message);
        // Update canSendText to true ONLY when inbound message is received (24-hour window resets)
        // Outbound messages (including templates) should not change canSendText
        if (message.direction === 'inbound') {
          // Create a new object to ensure Immer detects the change
          state.canSendText = {
            ...state.canSendText,
            [conversationId]: true,
          };
        }
      }

      // Update or create conversation
      let conversation = state.conversations.find((c) => c.id === conversationId);

      if (!conversation) {
        // Create new conversation if it doesn't exist
        // If worker info is provided (e.g., for temporary conversations), use it; otherwise use empty object
        const workerInfo = worker ? {
          externalId: worker.externalId,
          firstName: worker.firstName,
          lastName: worker.lastName,
          avatarUrl: worker.avatarUrl,
          phoneNumber: worker.phoneNumber,
          isUnknown: worker.isUnknown || false,
        } : ({} as any);

        const newConversation: ConversationListItemDto = {
          id: conversationId,
          task: ConversationTask.INBOX,
          status: ConversationStatus.PENDING,
          unreadCount: message.direction === 'inbound' && conversationId !== state.activeConversationId ? 1 : 0,
          lastMessage: {
            content: message.content || '',
            timestamp: new Date(message.sentAt * 1000),
            direction: message.direction as MessageDirection,
            messageType: message.messageType as MessageType,
          },
          lastMessageAt: message.sentAt,
          lastInboundMessageAt: message.direction === 'inbound' ? new Date(message.sentAt * 1000) : new Date(),
          worker: workerInfo,
          assignedToUserId: undefined,
          aiHandled: false,
          whatsappPhone: worker?.phoneNumber || '', // Use worker phone if available
          createdAt: message.sentAt,
          canSendText: message.direction === 'inbound' ? true : false,
          remainingHoursToSendTxt: 0,
        };

        // Add new conversation to the beginning of the list
        state.conversations = [newConversation, ...state.conversations];
        state.conversationsTotal = (state.conversationsTotal || 0) + 1;

        // Update canSendText state for the new conversation
        state.canSendText = {
          ...state.canSendText,
          [conversationId]: newConversation.canSendText,
        };

        conversation = newConversation;
      } else {
        // Update existing conversation
        conversation.lastMessageAt = message.sentAt;

        // Update last message preview
        conversation.lastMessage = {
          content: message.content || '',
          timestamp: new Date(message.sentAt * 1000),
          direction: message.direction as MessageDirection,
          messageType: message.messageType as MessageType,
          ...(message.direction === 'outbound' && message.status ? { status: message.status as MessageStatus } : {}),
        };

        // Update canSendText in conversation object when inbound message is received
        if (!exists && message.direction === 'inbound') {
          conversation.canSendText = true;
        }

        // Increment unread count if message is inbound and not from current user
        if (message.direction === 'inbound' && conversationId !== state.activeConversationId) {
          conversation.unreadCount = (conversation.unreadCount || 0) + 1;
        }

        // Move conversation to the top of the list when a new message is received
        // This ensures conversations with new messages appear first
        // Move to top for any new message (not just when message doesn't exist in array)
        if (!exists) {
          const conversationIndex = state.conversations.findIndex((c) => c.id === conversationId);
          if (conversationIndex > 0) {
            // Remove conversation from its current position and move it to the top
            // Use the conversation variable directly since it's already been updated
            state.conversations = [
              conversation,
              ...state.conversations.slice(0, conversationIndex),
              ...state.conversations.slice(conversationIndex + 1),
            ];
          }
        } else if (message.direction === 'inbound' && conversationId !== state.activeConversationId) {
          // Also move to top when receiving a new inbound message for non-active conversation
          // This ensures the conversation with new unread messages appears at the top
          const conversationIndex = state.conversations.findIndex((c) => c.id === conversationId);
          if (conversationIndex > 0) {
            state.conversations = [
              conversation,
              ...state.conversations.slice(0, conversationIndex),
              ...state.conversations.slice(conversationIndex + 1),
            ];
          }
        }
      }
    },
    handleRealtimeMessageStatus: (
      state,
      action: PayloadAction<{ conversationId: string; messageId: string; status: string; deliveredAt?: number; readAt?: number }>
    ) => {
      const { conversationId, messageId, status, deliveredAt, readAt } = action.payload;

      // Update message status in loaded messages
      if (state.messages[conversationId]) {
        const message = state.messages[conversationId].find((m) => m.id === messageId);
        if (message) {
          message.status = status as MessageStatus;
          if (deliveredAt) message.deliveredAt = new Date(deliveredAt * 1000);
          if (readAt) message.readAt = new Date(readAt * 1000);
        }
      }
    },
    handleRealtimeConversationAssigned: (
      state,
      action: PayloadAction<{ conversationId: string; assignedToUserId: number | null; aiHandled: boolean }>
    ) => {
      const { conversationId, assignedToUserId, aiHandled } = action.payload;

      const conversation = state.conversations.find((c) => c.id === conversationId);
      if (conversation) {
        conversation.assignedToUserId = assignedToUserId;
        conversation.aiHandled = aiHandled;
      }
    },
    updateMessageId: (
      state,
      action: PayloadAction<{ conversationId: string; tempMessageId: string; newMessageId: string }>
    ) => {
      const { conversationId, tempMessageId, newMessageId } = action.payload;

      // Update message ID in loaded messages
      if (state.messages[conversationId]) {
        const message = state.messages[conversationId].find((m) => m.id === tempMessageId);
        if (message) {
          message.id = newMessageId;
        }
      }
    },
    removeMessage: (
      state,
      action: PayloadAction<{ conversationId: string; messageId: string }>
    ) => {
      const { conversationId, messageId } = action.payload;

      // Remove message from loaded messages
      if (state.messages[conversationId]) {
        state.messages[conversationId] = state.messages[conversationId].filter(
          (m) => m.id !== messageId
        );
      }
    },
    handleRealtimeTaskStatus: (
      state,
      action: PayloadAction<{ conversationId: string; currentStatus: ConversationStatus; currentTask?: ConversationTask }>
    ) => {
      const { conversationId, currentStatus, currentTask } = action.payload;

      // Find the conversation index to update it immutably
      const conversationIndex = state.conversations.findIndex((c) => c.id === conversationId);
      if (conversationIndex !== -1) {
        // Create a new array with updated conversation to ensure Immer detects the change
        state.conversations = state.conversations.map((conv, index) =>
          index === conversationIndex
            ? {
              ...conv,
              status: currentStatus,
              ...(currentTask !== undefined && { task: currentTask })
            }
            : conv
        );
      }
    },
    updateCanSendText: (
      state,
      action: PayloadAction<{ conversationId: string; canSendText: boolean }>
    ) => {
      const { conversationId, canSendText } = action.payload;
      state.canSendText[conversationId] = canSendText;
    },
    updateConversationDetails: (
      state,
      action: PayloadAction<{ conversation: ConversationListItemDto }>
    ) => {
      const { conversation } = action.payload;
      const conversationIndex = state.conversations.findIndex((c) => c.id === conversation.id);
      if (conversationIndex !== -1) {
        // Update the conversation with full details
        state.conversations[conversationIndex] = conversation;
        // Update canSendText if needed
        if (conversation.canSendText !== undefined) {
          state.canSendText[conversation.id] = conversation.canSendText;
        }
      }
    },
  },
  extraReducers: (builder) => {
    // Get conversations
    builder
      .addCase(getConversations.pending, (state, action) => {
        const isAppending = action.meta.arg?.append || false;
        if (isAppending) {
          state.isLoadingMoreConversations = true;
        } else {
          state.isLoadingConversations = true;
        }
        state.error = null;
      })
      .addCase(getConversations.fulfilled, (state, action) => {
        const isAppending = action.payload.append || false;

        if (isAppending) {
          state.isLoadingMoreConversations = false;
          // Append new conversations, avoiding duplicates
          const existingIds = new Set(state.conversations.map(c => c.id));
          const newConversations = action.payload.conversations.filter(c => !existingIds.has(c.id));
          state.conversations = [...state.conversations, ...newConversations];

          // Update canSendText for new conversations
          newConversations.forEach(conv => {
            state.canSendText[conv.id] = conv.canSendText;
          });
        } else {
          state.isLoadingConversations = false;
          state.conversations = action.payload.conversations;

          // Update canSendText for all conversations
          action.payload.conversations.forEach(conv => {
            state.canSendText[conv.id] = conv.canSendText;
          });
        }

        state.conversationsTotal = action.payload.total;
        state.conversationsPage = action.payload.page;
        state.rtmToken = action.payload.rtmToken;
        state.channelId = action.payload.channelId;
      })
      .addCase(getConversations.rejected, (state, action) => {
        state.isLoadingConversations = false;
        state.isLoadingMoreConversations = false;
        Toast.Error({ content: action.error.message || 'Failed to load conversations' });
      });

    // Get messages
    builder
      .addCase(getMessages.pending, (state) => {
        state.isLoadingMessages = true;
        state.error = null;
      })
      .addCase(getMessages.fulfilled, (state, action) => {
        state.isLoadingMessages = false;
        const { conversationId, messages, total } = action.payload;
        state.messages[conversationId] = messages;
        state.messagesTotal[conversationId] = total;
      })
      .addCase(getMessages.rejected, (state, action) => {
        state.isLoadingMessages = false;
        Toast.Error({ content: action.error.message || 'Failed to load messages' });
      });
    builder
      .addCase(getMessagesCvSearch.pending, (state) => {
        state.isLoadingMessages = true;
        state.error = null;
      })
      .addCase(getMessagesCvSearch.fulfilled, (state, action) => {
        state.isLoadingMessages = false;
        const { workerExternalId, messages, total } = action.payload;
        state.messages[workerExternalId] = messages;
        state.messagesTotal[workerExternalId] = total;
      })
      .addCase(getMessagesCvSearch.rejected, (state, action) => {
        state.isLoadingMessages = false;
        Toast.Error({ content: action.error.message || 'Failed to load messages' });
      });

    // Send message
    builder
      .addCase(sendMessage.pending, (state, action) => {
        state.isSendingMessage = true;
        const payload = action.meta.arg as any;
        const conversationId = payload.conversationId as string;

        // Initialize messages array if it doesn't exist (for first message scenario)
        if (conversationId && !state.messages[conversationId]) {
          state.messages[conversationId] = [];
        }

        let message: SendMessageResponse = {
          content: replaceVariablesInTemplate(payload.content, payload.templateData.jobDetails, payload.templateData.partner, [payload.templateData.selectedApplicant], payload.templateData.partnerUser),
          conversationId: conversationId,
          createdAt: new Date(),
          direction: MessageDirection.OUTBOUND,
          id: payload.tempMessageId,
          messageType: MessageType.TEXT,
          sentAt: Math.floor(Date.now() / 1000),
          sentByUserId: 0,
          sentByUserExternalId: 'id',
          status: MessageStatus.SENT,
        };

        if (conversationId && state.messages[conversationId]) {
          state.messages[conversationId].unshift(message);
        }
      })
      .addCase(sendMessage.fulfilled, (state, action) => {
        state.isSendingMessage = false;
        const { conversationId, tempMessageId, templateData, message } = action.payload;
        const payload = action.meta.arg as any;

        // Update message ID from temporary ID to the real ID from the response
        if (conversationId && tempMessageId && message?.id && state.messages[conversationId]) {
          // Create a new array with updated message to ensure Immer detects the change
          state.messages[conversationId] = state.messages[conversationId].map((m) =>
            m.id === tempMessageId
              ? { ...m, id: String(message.id) }
              : m
          );
        }

        // Get the sent message to use for updating the conversation
        const sentMessage = conversationId && state.messages[conversationId]?.find((m) =>
          m.id === String(message.id) || m.id === tempMessageId
        );

        // Find existing conversation
        const conversationIndex = conversationId ? state.conversations.findIndex((c) => c.id === conversationId) : -1;
        const existingConversation = conversationIndex !== -1 ? state.conversations[conversationIndex] : null;

        // Use sentMessage if found, otherwise fall back to message from response
        // This ensures templates work even if message matching fails
        const messageToUse = sentMessage || (message ? {
          ...message,
          sentAt: message.sentAt || Math.floor(Date.now() / 1000),
          content: message.content || '',
        } : null);

        if (conversationId && existingConversation && messageToUse) {
          // Update existing conversation with new message details
          existingConversation.lastMessageAt = messageToUse.sentAt;
          existingConversation.lastMessage = {
            content: messageToUse.content || '',
            timestamp: new Date(messageToUse.sentAt * 1000),
            direction: messageToUse.direction as MessageDirection,
            messageType: messageToUse.messageType as MessageType,
          };

          // Move conversation to the top of the list (same behavior for regular messages and templates)
          // This ensures templates move to top just like regular messages
          // Move to top if not already there (conversationIndex > 0)
          if (conversationIndex > 0) {
            state.conversations = [
              existingConversation,
              ...state.conversations.slice(0, conversationIndex),
              ...state.conversations.slice(conversationIndex + 1),
            ];
          }
        } else if (conversationId && !existingConversation && messageToUse) {
          // Create new conversation if it doesn't exist (first message scenario)
          const newConversation: ConversationListItemDto = {
            id: conversationId,
            task: ConversationTask.INBOX,
            status: ConversationStatus.PENDING,
            unreadCount: 0,
            lastMessage: {
              content: messageToUse.content || '',
              timestamp: new Date(messageToUse.sentAt * 1000),
              direction: messageToUse.direction as MessageDirection,
              messageType: messageToUse.messageType as MessageType,
            },
            lastMessageAt: messageToUse.sentAt,
            lastInboundMessageAt: new Date(),
            worker: {} as any,
            assignedToUserId: undefined,
            aiHandled: false,
            whatsappPhone: '', // Will be populated when conversation details are fetched
            createdAt: messageToUse.sentAt,
            canSendText: false, // Templates don't open 24-hour window
            remainingHoursToSendTxt: 0,
          };

          // Add new conversation to the beginning of the list
          state.conversations = [newConversation, ...state.conversations];
          state.conversationsTotal = (state.conversationsTotal || 0) + 1;

          // Update canSendText state for the new conversation
          state.canSendText = {
            ...state.canSendText,
            [conversationId]: newConversation.canSendText,
          };
        }

        // Preserve canSendText when sending a template (templates don't affect 24-hour window)
        // Templates should not change canSendText - use conversation object as source of truth
        if (conversationId && payload?.templateId) {
          const conversation = state.conversations.find(c => c.id === conversationId);

          if (conversation) {
            // Use conversation object's canSendText as source of truth
            // This ensures templates don't incorrectly change the value
            const valueToPreserve = conversation.canSendText !== undefined ? conversation.canSendText : false;

            // Sync Redux state with conversation object
            state.canSendText = {
              ...state.canSendText,
              [conversationId]: valueToPreserve,
            };
          }
        }
      })
      .addCase(sendMessage.rejected, (state, action) => {
        state.isSendingMessage = false;
        const payload = action.meta.arg as any;
        const conversationId = payload?.conversationId as string;
        const tempMessageId = payload?.tempMessageId as string;

        // Remove the optimistically added message on error
        if (conversationId && tempMessageId && state.messages[conversationId]) {
          state.messages[conversationId] = state.messages[conversationId].filter(
            (m) => m.id !== tempMessageId
          );
        }

        Toast.Error({ content: action.error.message || 'Failed to send message' });
      });

    // Assign conversation
    builder
      .addCase(assignConversation.fulfilled, (state, action) => {
        const { conversationId, assignedToUserId, aiHandled } = action.payload;
        const conversation = state.conversations.find((c) => c.id === conversationId);
        if (conversation) {
          conversation.assignedToUserId = assignedToUserId;
          conversation.aiHandled = aiHandled;
        }
      })
      .addCase(assignConversation.rejected, (state, action) => {
        Toast.Error({ content: action.error.message || 'Failed to assign conversation' });
      });

    // Get application history
    builder
      .addCase(getApplicationHistory.pending, (state) => {
        state.isLoadingApplicationHistory = true;
      })
      .addCase(getApplicationHistory.fulfilled, (state, action) => {
        state.isLoadingApplicationHistory = false;
        state.applicationHistory = action.payload;
        state.applicationHistoryTotal = action.payload.total;
      })
      .addCase(getApplicationHistory.rejected, (state, action) => {
        state.isLoadingApplicationHistory = false;
        Toast.Error({ content: action.error.message });
      });

    // Get recent application details
    builder
      .addCase(getRecentApplicationDetails.pending, (state) => {
        state.isLoadingRecentApplicationDetails = true;
      })
      .addCase(getRecentApplicationDetails.fulfilled, (state, action) => {
        state.isLoadingRecentApplicationDetails = false;
        state.recentApplicationDetails = action.payload;
      })
      .addCase(getRecentApplicationDetails.rejected, (state, action) => {
        state.isLoadingRecentApplicationDetails = false;
        Toast.Error({ content: action.error.message });
      });

    // Get conversation details
    builder
      .addCase(getConversationDetails.fulfilled, (state, action) => {
        const conversation = action.payload;
        const conversationIndex = state.conversations.findIndex((c) => c.id === conversation.id);
        if (conversationIndex !== -1) {
          // Update the conversation with full details
          state.conversations[conversationIndex] = conversation;
          // Update canSendText if needed
          if (conversation.canSendText !== undefined) {
            state.canSendText[conversation.id] = conversation.canSendText;
          }
        }
      })
      .addCase(getConversationDetails.rejected, (state, action) => {
        // Silently fail - conversation will remain with minimal data
        console.warn('[WhatsApp Inbox] Failed to fetch conversation details:', action.error.message);
      });
  },
});

export const {
  setActiveConversation,
  setActiveWhatsappInboxTab,
  setActiveWhatsappProfileTab,
  setFilters,
  resetFilters,
  clearError,
  handleRealtimeMessage,
  handleRealtimeMessageStatus,
  handleRealtimeConversationAssigned,
  updateMessageId,
  removeMessage,
  handleRealtimeTaskStatus,
  updateCanSendText,
  updateConversationDetails,
} = whatsappInboxSlice.actions;

export default whatsappInboxSlice.reducer;
